import { useMemo } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MainLayout } from "@/components/layout/MainLayout";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Invoice, InvoiceLineItem } from "@/types/billing";
import {
  ArrowLeft,
  FileText,
  Download,
  Printer,
  Send,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Building2,
  Calendar,
  DollarSign,
} from "lucide-react";

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getStatusBadge = (status: Invoice["status"]) => {
  switch (status) {
    case "paid":
      return (
        <Badge className="bg-green-100 text-green-800 gap-1">
          <CheckCircle className="h-3 w-3" />
          Paid
        </Badge>
      );
    case "sent":
      return (
        <Badge className="bg-blue-100 text-blue-800 gap-1">
          <Send className="h-3 w-3" />
          Sent
        </Badge>
      );
    case "draft":
      return (
        <Badge variant="secondary" className="gap-1">
          <Clock className="h-3 w-3" />
          Draft
        </Badge>
      );
    case "overdue":
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="h-3 w-3" />
          Overdue
        </Badge>
      );
    case "cancelled":
      return (
        <Badge variant="outline" className="gap-1">
          <XCircle className="h-3 w-3" />
          Cancelled
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

interface InvoiceWithDetails extends Invoice {
  tenant?: {
    name: string;
    email?: string;
    address?: string;
  };
}

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { isAgency, loading: authLoading } = useAuth();

  // Fetch invoice details
  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ["invoice-detail", invoiceId],
    queryFn: async (): Promise<InvoiceWithDetails | null> => {
      if (!invoiceId) return null;

      const { data, error } = await supabase
        .from("calie_invoices")
        .select(`
          *,
          tenant:calie_tenants(
            tenant_name
          )
        `)
        .eq("id", invoiceId)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return null; // Not found
        }
        throw error;
      }

      // Parse line items from metadata if available
      const metadata = data.metadata as Record<string, unknown> | null;
      const lineItems: InvoiceLineItem[] = (metadata?.line_items as InvoiceLineItem[]) || [];

      return {
        id: data.id,
        tenant_id: data.tenant_id,
        tenant_name: (data.tenant as any)?.tenant_name || "Unknown Client",
        invoice_number: data.invoice_number,
        amount: data.total,
        base_amount: data.subtotal,
        markup_amount: data.tax,
        status: data.status as Invoice["status"],
        period_start: data.billing_period_start,
        period_end: data.billing_period_end,
        due_date: data.due_date,
        paid_date: data.paid_at,
        line_items: lineItems,
        created_at: data.created_at,
        tenant: {
          name: (data.tenant as any)?.tenant_name || "Unknown Client",
        },
      };
    },
    enabled: !!invoiceId && isAgency(),
  });

  // Generate line items if not present (compute from usage)
  const lineItems = useMemo(() => {
    if (invoice?.line_items && invoice.line_items.length > 0) {
      return invoice.line_items;
    }

    // Generate placeholder line items from amounts
    const items: InvoiceLineItem[] = [];

    if (invoice?.base_amount && invoice.base_amount > 0) {
      items.push({
        id: "base",
        description: "Platform Services",
        service_type: "platform",
        quantity: 1,
        unit: "month",
        unit_price: invoice.base_amount,
        total: invoice.base_amount,
      });
    }

    if (invoice?.markup_amount && invoice.markup_amount > 0) {
      items.push({
        id: "markup",
        description: "Agency Service Fee",
        service_type: "markup",
        quantity: 1,
        unit: "fee",
        unit_price: invoice.markup_amount,
        total: invoice.markup_amount,
      });
    }

    return items;
  }, [invoice]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // In a real implementation, this would call an API to generate a PDF
    // For now, trigger print which allows saving as PDF
    window.print();
  };

  // Redirect if not an agency
  if (!authLoading && !isAgency()) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10" />
            <Skeleton className="h-8 w-48" />
          </div>
          <Skeleton className="h-[400px] w-full" />
        </div>
      </MainLayout>
    );
  }

  if (error || !invoice) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Card>
            <CardContent className="py-10 text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">
                {error
                  ? `Error loading invoice: ${(error as Error).message}`
                  : "Invoice not found"}
              </p>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 print:space-y-4">
        {/* Header - Hide on print */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                Invoice {invoice.invoice_number}
              </h1>
              <p className="text-muted-foreground">
                {invoice.tenant_name || "Client Invoice"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        {/* Invoice Card */}
        <Card className="print:shadow-none print:border-none">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <CardTitle className="text-3xl font-bold flex items-center gap-2 print:text-2xl">
                  <FileText className="h-8 w-8 text-primary print:hidden" />
                  INVOICE
                </CardTitle>
                <CardDescription className="text-lg mt-1">
                  {invoice.invoice_number}
                </CardDescription>
              </div>
              <div className="text-right">
                {getStatusBadge(invoice.status)}
                <p className="text-sm text-muted-foreground mt-2">
                  Issue Date: {formatDate(invoice.created_at)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Due Date: {formatDate(invoice.due_date)}
                </p>
                {invoice.paid_date && (
                  <p className="text-sm text-green-600 font-medium">
                    Paid: {formatDate(invoice.paid_date)}
                  </p>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Bill To */}
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Bill To
                </h3>
                <div className="flex items-start gap-2">
                  <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">{invoice.tenant_name}</p>
                    {invoice.tenant?.email && (
                      <p className="text-sm text-muted-foreground">
                        {invoice.tenant.email}
                      </p>
                    )}
                    {invoice.tenant?.address && (
                      <p className="text-sm text-muted-foreground">
                        {invoice.tenant.address}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Billing Period
                </h3>
                <div className="flex items-start gap-2">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">
                      {formatDate(invoice.period_start)} -{" "}
                      {formatDate(invoice.period_end)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Line Items */}
            <div>
              <h3 className="text-lg font-medium mb-4">Invoice Details</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50%]">Description</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.description}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.service_type}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {item.quantity} {item.unit}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item.unit_price)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Separator />

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-full max-w-xs space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(invoice.base_amount)}</span>
                </div>
                {invoice.markup_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Agency Fee
                    </span>
                    <span className="text-green-600">
                      +{formatCurrency(invoice.markup_amount)}
                    </span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span className="flex items-center gap-1">
                    <DollarSign className="h-5 w-5" />
                    Total Due
                  </span>
                  <span>{formatCurrency(invoice.amount)}</span>
                </div>
              </div>
            </div>

            {/* Payment Status */}
            {invoice.status === "paid" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium text-green-800">
                  This invoice has been paid
                </p>
                {invoice.paid_date && (
                  <p className="text-sm text-green-600">
                    Payment received on {formatDate(invoice.paid_date)}
                  </p>
                )}
              </div>
            )}

            {invoice.status === "overdue" && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <p className="font-medium text-red-800">
                  This invoice is overdue
                </p>
                <p className="text-sm text-red-600">
                  Please contact the client to arrange payment
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer Note - Print Only */}
        <div className="hidden print:block text-center text-sm text-muted-foreground pt-4">
          <p>Thank you for your business!</p>
          <p>For questions about this invoice, please contact your account manager.</p>
        </div>
      </div>
    </MainLayout>
  );
}
